import React, { useCallback, useEffect, useState } from 'react';
import { Button, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { FileText, Globe, Plus, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CreateKnowledgeModal } from '../../../components/agents/CreateKnowledgeModal';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { useNotification } from '../../../hooks/useNotification';
import {
  deleteKnowledge,
  getKnowledge,
  listKnowledge,
  resyncKnowledge,
} from '../../../services/api';
import type { Knowledge, KnowledgeSourceType } from '../../../types';
import { ProjectEmptyState } from './ProjectEmptyState';

const KNOWLEDGE_POLL_INTERVAL_MS = 3500;

const STATUS_COLORS = {
  success: 'success',
  partial: 'warning',
  failed: 'error',
  processing: 'info',
  deleting: 'error',
} as const;

const SOURCE_ICONS: Record<KnowledgeSourceType, React.ReactNode> = {
  file: <Upload size={16} />,
  url: <Globe size={16} />,
  text: <FileText size={16} />,
};

const isSyncing = (knowledge: Knowledge) =>
  !knowledge.syncStatus ||
  knowledge.syncStatus === 'pending' ||
  knowledge.syncStatus === 'processing';

const getErrorMessage = (errorSummary?: string | null): string | null => {
  if (!errorSummary) return null;
  let message = errorSummary;
  try {
    const parsed = JSON.parse(errorSummary) as { error?: string; errors?: string[] };
    message = parsed.error || parsed.errors?.join('; ') || errorSummary;
  } catch {
    // errorSummary is plain text
  }
  return message.length > 180 ? `${message.slice(0, 180).trimEnd()}...` : message;
};

interface ProjectKnowledgePanelProps {
  projectId: string;
}

/** Knowledge shared by every conversation in the project (searched via knowledge_search). */
export const ProjectKnowledgePanel: React.FC<ProjectKnowledgePanelProps> = ({ projectId }) => {
  const { t } = useTranslation();
  const { error: showError } = useNotification();
  const [items, setItems] = useState<Knowledge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Knowledge | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resyncingIds, setResyncingIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setIsLoading(true);
    const response = await listKnowledge('project', undefined, undefined, projectId);
    if (response.success && response.data) {
      setItems(response.data);
    } else {
      showError(response.error ?? t('knowledge.errors.loadFailed'));
    }
    setIsLoading(false);
  }, [projectId, showError, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const pollingKey = items
    .filter(isSyncing)
    .map((item) => item.publicId)
    .sort()
    .join('|');

  useEffect(() => {
    const ids = pollingKey ? pollingKey.split('|') : [];
    if (ids.length === 0) return;

    let cancelled = false;
    const poll = async () => {
      const responses = await Promise.all(ids.map((id) => getKnowledge(id)));
      if (cancelled) return;
      const updated = new Map(
        responses.flatMap((response) =>
          response.success && response.data
            ? [[response.data.publicId, response.data] as const]
            : []
        )
      );
      if (updated.size > 0) {
        setItems((current) => current.map((item) => updated.get(item.publicId) ?? item));
      }
    };

    const intervalId = window.setInterval(() => void poll(), KNOWLEDGE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [pollingKey]);

  const handleResync = async (knowledge: Knowledge) => {
    setResyncingIds((prev) => new Set(prev).add(knowledge.publicId));
    const response = await resyncKnowledge(knowledge.publicId);
    if (response.success && response.data) {
      setItems((current) =>
        current.map((item) => (item.publicId === knowledge.publicId ? response.data! : item))
      );
    } else {
      showError(response.error ?? t('knowledge.errors.resyncFailed'));
    }
    setResyncingIds((prev) => {
      const next = new Set(prev);
      next.delete(knowledge.publicId);
      return next;
    });
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setIsDeleting(true);
    const response = await deleteKnowledge(toDelete.publicId);
    if (response.success) {
      setItems((current) => current.filter((item) => item.publicId !== toDelete.publicId));
    } else {
      showError(response.error ?? t('knowledge.errors.deleteFailed'));
    }
    setIsDeleting(false);
    setToDelete(null);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-600 dark:text-slate-400">{t('projects.knowledge.hint')}</p>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setIsCreateOpen(true)}
          className="shrink-0"
        >
          {t('projects.knowledge.add')}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <CircularProgress size={24} />
        </div>
      ) : items.length === 0 ? (
        <ProjectEmptyState
          title={t('projects.knowledge.emptyTitle')}
          description={t('projects.knowledge.emptyDescription')}
        />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          {items.map((item) => {
            const status = item.syncStatus || 'pending';
            const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS] ?? 'default';
            const isResyncing = resyncingIds.has(item.publicId);
            const canResync = (status === 'failed' || status === 'partial') && !isResyncing;
            const errorMessage =
              status === 'failed' || status === 'partial'
                ? getErrorMessage(item.errorSummary)
                : null;
            return (
              <li
                key={item.publicId}
                className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-slate-700"
              >
                <span className="shrink-0 text-gray-400 dark:text-slate-500">
                  {SOURCE_ICONS[item.sourceType] ?? SOURCE_ICONS.text}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-gray-900 dark:text-slate-100">
                    {item.name}
                  </p>
                  {item.description && (
                    <p className="truncate text-xs text-gray-500 dark:text-slate-400">
                      {item.description}
                    </p>
                  )}
                  {errorMessage && (
                    <p className="truncate text-xs text-red-600 dark:text-red-300">
                      {errorMessage}
                    </p>
                  )}
                </div>
                <Chip size="small" color={color} label={t(`knowledge.status.${status}`)} />
                {(canResync || isResyncing) && (
                  <Tooltip title={t('knowledge.actions.resyncKnowledge')}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t('knowledge.actions.resyncKnowledge')}
                        disabled={isResyncing}
                        onClick={() => void handleResync(item)}
                        className="text-gray-400 hover:text-blue-600 dark:text-slate-500 dark:hover:text-blue-400"
                      >
                        {isResyncing ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={t('knowledge.actions.deleteKnowledge')}>
                  <IconButton
                    size="small"
                    aria-label={t('knowledge.actions.deleteKnowledge')}
                    onClick={() => setToDelete(item)}
                    className="text-gray-400 hover:text-red-600 dark:text-slate-500 dark:hover:text-red-400"
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}

      <CreateKnowledgeModal
        open={isCreateOpen}
        projectId={projectId}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(knowledge) => {
          setItems((current) => [knowledge, ...current]);
          setIsCreateOpen(false);
        }}
      />
      <ConfirmDialog
        open={Boolean(toDelete)}
        title={t('knowledge.deleteDialog.title')}
        message={`${t('knowledge.deleteDialog.beforeName')} "${toDelete?.name ?? ''}" ${t('knowledge.deleteDialog.afterName')}`}
        confirmText={t('knowledge.actions.delete')}
        cancelText={t('common.cancel')}
        confirmColor="error"
        loading={isDeleting}
        onClose={() => setToDelete(null)}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
};
